/**
 * Renders an ISO date the way the bot's backend does: "Sun 30 Aug" / "יום א׳ 30 באוג׳".
 *
 * The cards used to print raw `2026-08-30` strings, which hid exactly the bug
 * this exists to prevent — a date whose weekday is wrong looks identical to a
 * correct one until someone checks a calendar. Showing the weekday on the card
 * makes the mismatch visible where users actually look.
 *
 * LOCALE MATTERS. This was written with hardcoded English names, on the premise
 * that they matched the BE-built `date_range_display` string in the chat text.
 * That stopped being true in the same release: the BE now formats that string in
 * the conversation language (`buildDateRangeDisplay(..., { locale })`), so a
 * Hebrew conversation showed "יום א׳ 30 באוג׳" in the text and "Sun 30 Aug" on
 * the card directly beneath it. Pass the site/conversation language so card and
 * text read the same, which is the whole point of showing the weekday at all.
 *
 * `Intl` is used exactly as the BE does it (same options, same UTC anchor), so
 * the two sides agree without a shared name table. Falls back to English short
 * names if the runtime has no data for the locale.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatBotDate(
  value?: string | null,
  locale?: string | null,
): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!match) return String(value);

  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return String(value);

  // UTC so the rendered weekday never shifts with the viewer's timezone.
  const date = new Date(Date.UTC(year, month - 1, day));

  // English goes through the hand-built names, NOT Intl — exactly as the BE does
  // it (`buildDateRangeDisplay`: `locale && locale !== 'en' ? localized : …`).
  // `Intl` with en-US renders "Sun Aug 30" while the BE prints "Sun 30 Aug", so
  // using Intl for English would reintroduce the very card/text mismatch this
  // change exists to remove.
  const primary = locale ? String(locale).toLowerCase().split(/[-_]/)[0] : '';
  if (locale && primary && primary !== 'en') {
    try {
      // The site stores `he_IL` / `en_US`; BCP 47 wants `he-IL`. Passing the
      // underscore form throws RangeError, which would silently drop every
      // localized card back to English.
      const bcp47 = String(locale).replace(/_/g, '-');
      const weekday = new Intl.DateTimeFormat(bcp47, {
        weekday: 'short',
        timeZone: 'UTC',
      }).format(date);
      const dayMonth = new Intl.DateTimeFormat(bcp47, {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }).format(date);
      return `${weekday} ${dayMonth}`;
    } catch {
      // Unknown locale — fall through to the English short names below.
    }
  }

  return `${WEEKDAYS[date.getUTCDay()]} ${day} ${MONTHS[month - 1]}`;
}
